import { apiClient } from './api';
import {
  CuisinePromptSession,
  StartCuisinePromptRequest,
  StartCuisinePromptResponse,
  CuisinePromptIterateRequest,
  CuisinePromptIterateResponse,
  BatchCuisineAnalysisRequest,
  BatchCuisineAnalysisResponse,
  CuisinePrompt,
} from '../models/cuisinePromptStudio';

const STORAGE_KEY = 'cuisinePromptStudioSessions';

class CuisinePromptStudioService {
  // API calls
  async startSession(request: StartCuisinePromptRequest): Promise<StartCuisinePromptResponse> {
    const response = await apiClient.post('/api/cuisine-prompt-studio/start', request);
    return response.data;
  }

  async iterate(
    sessionId: string,
    request: CuisinePromptIterateRequest
  ): Promise<CuisinePromptIterateResponse> {
    const response = await apiClient.post(
      `/api/cuisine-prompt-studio/${sessionId}/iterate`,
      request
    );
    
    // Debug logging
    console.log('Cuisine Studio Iterate Request:', request);
    console.log('Cuisine Studio Iterate Response:', {
      sessionId: response.data.sessionId,
      phaseId: response.data.phaseId,
      result: response.data.result,
      promptContent: response.data.generatedPrompt?.content,
      iterations: response.data.generatedPrompt?.iterations,
      fullResponse: response.data
    });
    
    // Log warning if content is empty
    if (response.data.generatedPrompt && (!response.data.generatedPrompt.content || response.data.generatedPrompt.content.trim() === '')) {
      console.warn('Empty content received from cuisine prompt API:', {
        agentType: request.agentType,
        phaseId: request.phaseId,
        hasResult: !!response.data.result,
        resultLength: response.data.result?.length || 0
      });
    }
    
    return response.data;
  }

  async analyzeBatch(request: BatchCuisineAnalysisRequest): Promise<BatchCuisineAnalysisResponse> {
    const response = await apiClient.post('/api/cuisine-prompt-studio/analyze-batch', request);
    return response.data;
  }

  async getSession(sessionId: string): Promise<CuisinePromptSession> {
    const response = await apiClient.get(`/api/cuisine-prompt-studio/${sessionId}`);
    return response.data;
  }

  // Helper agent execution
  async executeHelperAgent(
    agentType: string,
    sessionId: string,
    input: any
  ): Promise<any> {
    // Route to appropriate agent endpoint based on type
    switch (agentType) {
      case 'abstract-thinker':
        return this.executeAbstractThinker(input);
      case 'prompt-improvement':
        return this.executePromptImprovement(sessionId, input);
      case 'image-prompt':
        return this.executeImagePrompt(sessionId, input);
      case 'content-marketing':
        return this.executeContentMarketing(input);
      default:
        if (agentType.startsWith('cuisine-')) {
          return this.executeCuisineAgent(agentType, sessionId, input);
        }
        throw new Error(`Unknown agent type: ${agentType}`);
    }
  }

  private async executeAbstractThinker(input: any): Promise<any> {
    const response = await apiClient.post('/api/thinker/abstract/abstract-thinking/start', {
      initialQueryOrTopic: input.concept,
      depth: 'deep',
      temperature: 0.8,
      focusArea: 'culinary concepts and food experiences',
    });
    return response.data;
  }

  private async executePromptImprovement(sessionId: string, input: any): Promise<any> {
    const response = await apiClient.post('/api/agent/prompt/start', {
      prompt: input.prompt,
      targetModel: 'gpt-4',
      improvementType: 'clarity',
    });
    return response.data;
  }

  private async executeImagePrompt(sessionId: string, input: any): Promise<any> {
    const response = await apiClient.post('/api/imageprompt/session/start', {
      initialPrompt: input.prompt,
      style: 'food photography',
      focus: 'appetizing presentation',
    });
    return response.data;
  }

  private async executeContentMarketing(input: any): Promise<any> {
    const response = await apiClient.post('/api/marketing/session/start', {
      initialPrompt: input.prompt,
      contentType: 'food description',
      platform: input.platform || 'general',
    });
    return response.data;
  }

  private async executeCuisineAgent(
    agentType: string,
    sessionId: string,
    input: any
  ): Promise<any> {
    const cuisineType = agentType.replace('cuisine-', '');
    const endpoint = this.getCuisineEndpoint(cuisineType);
    
    const response = await apiClient.post(`${endpoint}/session/start`, {
      initialPrompt: input.prompt || input.concept,
      sessionType: cuisineType,
      temperature: input.temperature || 0.7,
      includeTraditionalElements: input.includeAuthenticity,
      includeModernInnovations: input.includeModern,
      emphasizeAuthenticity: input.includeAuthenticity,
      dietaryConsiderations: input.dietaryRestrictions || [],
      requiredIngredients: input.ingredients || [],
    });
    
    return response.data;
  }

  private getCuisineEndpoint(cuisineType: string): string {
    const endpoints: Record<string, string> = {
      'italian': '/api/italian-cuisine',
      'mexican': '/api/mexican-cuisine',
      'indian': '/api/indian-cuisine',
      'thai': '/api/thai-cuisine',
      'japanese': '/api/japanese-cuisine',
      'french': '/api/frenchcuisine',
      'korean': '/api/korean-cuisine',
      'chinese': '/api/chinesecuisine',
      'spanish': '/api/spanishcuisine',
      'caribbean': '/api/caribbeancuisine',
      'general': '/api/culinary',
    };
    return endpoints[cuisineType] || '/api/culinary';
  }

  // Local storage management
  saveSession(session: CuisinePromptSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  getLocalSession(sessionId: string): CuisinePromptSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  getAllSessions(): CuisinePromptSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  // Helper methods
  createLocalSession(concept: string, cuisines: string[], useCase: string): CuisinePromptSession {
    return {
      id: `cuisine-prompt-${Date.now()}`,
      concept,
      selectedCuisines: cuisines,
      targetUseCase: useCase as any,
      dietaryRestrictions: [],
      flavorProfile: {
        sweet: 50,
        sour: 50,
        salty: 50,
        bitter: 50,
        umami: 50,
        spicy: 50,
      },
      culturalAuthenticity: true,
      modernInterpretation: false,
      phases: [],
      generatedPrompts: [],
      settings: {
        temperature: 0.7,
        includeAuthenticity: true,
        includeModern: false,
        emphasizeTechniques: true,
        includeNutritional: false,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  addPromptToSession(session: CuisinePromptSession, prompt: CuisinePrompt): void {
    session.generatedPrompts.push(prompt);
    session.updatedAt = new Date().toISOString();
    this.saveSession(session);
  }

  updatePhaseStatus(
    session: CuisinePromptSession,
    phaseId: string,
    status: string,
    result?: any
  ): void {
    const phase = session.phases.find(p => p.id === phaseId);
    if (phase) {
      phase.status = status as any;
      if (result) {
        phase.result = result;
      }
      session.updatedAt = new Date().toISOString();
      this.saveSession(session);
    }
  }

  // Export functionality
  exportSession(session: CuisinePromptSession): string {
    const exportData = {
      session: {
        id: session.id,
        concept: session.concept,
        cuisines: session.selectedCuisines,
        useCase: session.targetUseCase,
        createdAt: session.createdAt,
      },
      prompts: session.generatedPrompts.map(p => ({
        type: p.type,
        content: p.content,
        metadata: p.metadata,
        score: p.score,
      })),
      settings: session.settings,
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  exportPromptsAsMarkdown(prompts: CuisinePrompt[]): string {
    let markdown = '# Cuisine Prompts\n\n';
    
    prompts.forEach((prompt, index) => {
      markdown += `## Prompt ${index + 1} - ${prompt.type}\n\n`;
      markdown += `${prompt.content}\n\n`;
      
      if (prompt.metadata.cuisine) {
        markdown += `**Cuisine:** ${prompt.metadata.cuisine}\n`;
      }
      if (prompt.metadata.dish) {
        markdown += `**Dish:** ${prompt.metadata.dish}\n`;
      }
      if (prompt.metadata.techniques?.length) {
        markdown += `**Techniques:** ${prompt.metadata.techniques.join(', ')}\n`;
      }
      if (prompt.metadata.ingredients?.length) {
        markdown += `**Key Ingredients:** ${prompt.metadata.ingredients.join(', ')}\n`;
      }
      if (prompt.metadata.culturalContext) {
        markdown += `**Cultural Context:** ${prompt.metadata.culturalContext}\n`;
      }
      
      markdown += '\n---\n\n';
    });
    
    return markdown;
  }
}

export const cuisinePromptStudioService = new CuisinePromptStudioService();